import { debounce } from 'lodash'
import { useCallback, useState } from 'react'
import useSWR from 'swr'

import { Article, Variation } from '../../../articlesContainer'
import {
	AiVariationContext,
	AiVariationsApiReply,
	CampaignAndSuggestionArticle,
	HideModalCallback,
} from '../aiVariationTypes'
import { doHttp, getApiUrl } from '../util'

const maxNumberOfVariationsShown = 8

type VariationChange = {
	[K in keyof Variation]?: string | boolean
}

const modifyAiSuggestions = (
	aiSuggestionArticle: Article,
	index: number,
	variationChanges: VariationChange | null = null,
): Article => {
	if (variationChanges) {
		aiSuggestionArticle.variations[index] = {
			...aiSuggestionArticle.variations[index],
			...variationChanges,
		} as Variation
	} else {
		// deleting a variation
		aiSuggestionArticle.variations = aiSuggestionArticle.variations.filter(
			(_, variationIndex) => variationIndex !== index,
		)
		// make sure indexes are correct
		aiSuggestionArticle.variations.forEach((v, i) => (v.variation_no = i))
	}

	return aiSuggestionArticle
}

export const useManageAiVariationsModal = (props: AiVariationContext & HideModalCallback) => {
	const generateMoreSuggestions = async (howMany: number) => {
		setCreationError('')
		setGeneratingMoreSuggestions(true)
		try {
			await doHttp('POST', getApiUrl(props), { numAds: howMany })
			setCreationError('')
		} catch (err) {
			setCreationError(err)
		}
		setGeneratingMoreSuggestions(false)
	}

	const fetcher = async (key: string): Promise<CampaignAndSuggestionArticle> => {
		const fetchExistingSuggestions = async () => doHttp<AiVariationsApiReply>('GET', key)

		let aiVariationReply = await fetchExistingSuggestions()

		// no suggestions yet and we're viewing this "propery" (without a token)
		if (!aiVariationReply.data.variations.length && !props.token) {
			await generateMoreSuggestions(maxNumberOfVariationsShown)
			aiVariationReply = await fetchExistingSuggestions()
		}

		setCampaignId(aiVariationReply.data.campaignId)
		setCampaignName(aiVariationReply.data.campaignName)

		return {
			campaignId: aiVariationReply.data.campaignId,
			campaignName: aiVariationReply.data.campaignName,
			article: {
				uid: '',
				origin: '',
				variations: aiVariationReply.data.variations.map(
					(aiSuggestion, idx) =>
						({
							// the following two lines are little bit ugly,  but we want
							// to utilize existing classes and components to edit the ai suggestions
							uid: `` + aiSuggestion.id, // anything as long as it's unique for any suggestion/variation
							id: aiSuggestion.id,
							comment: aiSuggestion.comment || '',
							variation_no: idx + 1, // variation_no's start from 1 instead of 0
							title: aiSuggestion.title,
							description: aiSuggestion.summary,
							image: aiSuggestion.image,
							cta: aiSuggestion.call_to_action,
							advertiser_name: aiSuggestion.advertiser_title || '',
							advertiser_title: aiSuggestion.advertiser_title || '',
							advertiser_theme: aiSuggestion.advertiser_theme || '',
							targetUrl: aiSuggestion.targetUrl || '',
							theme: aiSuggestion.advertiser_theme || '',
							impressionPixel: '',
							suggestion: false,
							is_active: false,
							approved: !!aiSuggestion.approved,
							dismissed: !!aiSuggestion.dismissed,
						} as Variation),
				),
			} as Article,
		}
	}
	const { data, mutate, error: fetchError, isValidating } = useSWR<CampaignAndSuggestionArticle>(
		getApiUrl(props),
		fetcher,
		{
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
		},
	)

	const [showTokenModal, setShowTokenModal] = useState(false)
	const [generatingMoreSuggestions, setGeneratingMoreSuggestions] = useState(false)
	const [refreshParentArticleWhenDone, setRefreshParentArticleWhenDone] = useState(false)
	const [pendingDebouncedVariation, setPendingDebouncedVariation] = useState<Variation | null>(null)
	const [campaignId, setCampaignId] = useState<number | undefined>(undefined)
	const [campaignName, setCampaignName] = useState<string | undefined>(undefined)
	const [creationError, setCreationError] = useState<string>('')

	const saveVariationImmediately = async (variation: Variation) => {
		if (!variation) {
			return
		}
		await doHttp('PATCH', getApiUrl(props, variation), {
			comment: variation.comment,
			advertiser_title: variation.advertiser_name,
			title: variation.title,
			advertiser_theme: variation.theme,
			summary: variation.description,
			call_to_action: variation.cta,
			approved: variation.approved,
			dismissed: variation.dismissed,
		})
	}

	const saveVariationDebounced = useCallback(debounce(saveVariationImmediately, 500), [])

	const modifyVariationInArticle = (variation_no: number, modification: VariationChange): Variation | null => {
		const index = variation_no - 1 // variation_no's start from 1
		const updatedArticle = modifyAiSuggestions(data!.article, index, modification)

		mutate(
			{
				campaignId: campaignId!,
				campaignName: campaignName!,
				article: updatedArticle,
			},
			false,
		)

		return updatedArticle.variations[index]
	}

	const updateVariationField = (
		artUid: string,
		variationNum: number,
		field: keyof Variation,
		content: string,
		instant?: boolean,
	): void => {
		// prevent flood of modifications when component is initializing (existing common components trigger change when instantiated)
		const existingValue = data?.article?.variations[variationNum - 1][field]
		if (existingValue === undefined || existingValue === content) {
			return
		}

		const variation: Variation = modifyVariationInArticle(variationNum, { [field]: content })!

		if (pendingDebouncedVariation && pendingDebouncedVariation.id !== variation.id) {
			saveVariationImmediately(pendingDebouncedVariation)
		}

		setPendingDebouncedVariation(variation)
		saveVariationDebounced(variation)
	}

	const handleApproveOrDisapproveAiSuggestion = async (
		variation: Variation,
		approve: boolean, // true = approve, false = dismiss
	) => {
		setRefreshParentArticleWhenDone(true)

		// this can be removed because we mutate(), but it is here just in case we want in the demo phase
		// to modify the functionality so that approved (or deleted) suggestions are still shown in ui after deletion.
		if (approve) {
			const approvedVariation = modifyVariationInArticle(variation.variation_no, {
				approved: !variation.approved,
				dismissed: false,
			})
			saveVariationImmediately(approvedVariation!)
		} else {
			const dismissedVariation = modifyVariationInArticle(variation.variation_no, {
				approved: false,
				dismissed: !variation.dismissed,
			})
			saveVariationImmediately(dismissedVariation!)
		}
	}

	const approveAndDismissAll = async () => {
		// dismissals
		await Promise.all(
			data!.article.variations
				.filter((variation) => variation.dismissed)
				.map((variation) => doHttp('DELETE', getApiUrl(props, variation), null, true)),
		)

		// approvals
		const approvedVariations = data!.article.variations.filter((variation) => variation.approved)
		for (const variation of approvedVariations) {
			await doHttp('POST', getApiUrl(props, variation))
		}
	}

	const handleLoadMore = async () => {
		await approveAndDismissAll()

		const undecided = data!.article.variations.filter((variation) => !variation.approved && !variation.dismissed)
		const amountToGenerate = maxNumberOfVariationsShown - (undecided.length || 0)

		if (amountToGenerate) {
			await generateMoreSuggestions(amountToGenerate)
			mutate()
		} else {
			alert('Please approve or dismiss existing suggestions.')
		}
	}

	const handleHideModalAndRefreshParentIfRequired = async (approveAndDismiss: boolean) => {
		if (approveAndDismiss) {
			await approveAndDismissAll()
		}
		if (props.token) {
			// viewing with token so we are not closing a modal
			mutate()
		} else {
			// viewing as "properly logged in" so we are closing a modal
			// reload parent articles if we've been clicking "approve or dismiss" or we clicked "save"-button (instead of close)
			props.hideModal && props.hideModal(refreshParentArticleWhenDone || approveAndDismiss)
		}
	}

	const numberOfApprovedOrDismissedSuggestions = (data?.article?.variations || []).filter(
		(variation) => variation.approved || variation.dismissed,
	).length

	const numberOfSuggestionsShown = (data?.article?.variations || []).length

	return {
		campaignId: props.campaignId || campaignId, // from props if logged in, from ai suggestions if not logged in
		campaignName: campaignName || '',

		viewingWithToken: !!props.token,
		loading: !data || isValidating || generatingMoreSuggestions,
		error: creationError || fetchError,
		generatingMoreSuggestions, // loading because generating (it's slow)

		// for approval components
		aiSuggestionArticle: data?.article,

		// for access token page's button
		numberOfApprovedOrDismissedSuggestions,
		numberOfApprovedArticles: (data?.article?.variations || []).filter((variation) => variation.approved).length,
		numberOfMissingArticles:
			maxNumberOfVariationsShown - numberOfSuggestionsShown + numberOfApprovedOrDismissedSuggestions,

		handleLoadMore,

		updateVariationField,
		handleApproveOrDisapproveAiSuggestion,

		showTokenModal,
		handleOpenTokenModal: () => setShowTokenModal(true),
		handleHideTokenModal: () => setShowTokenModal(false),

		handleHideModalAndRefreshParentIfRequired,
		refreshParentArticleWhenDone, // exposed for tests

		// dummy stuff required by ArticlePanel - these could be implemented in the future if UI functionality is expanded:
		duplicateVariation: () => {},
		removeVariation: () => {},
		addVariation: () => {},
	}
}
